// Settings hub section catalogue. One row per navigable settings surface.
// Drives the vertical sidebar nav and the ⌘K command palette. Order here is
// display order: grouped Workspace → Communications → Content & Data →
// Scheduling, with the admin-only Agents catalogue last.
import {
  Bot,
  Calendar,
  CalendarCheck,
  CreditCard,
  FileText,
  Layers,
  Mail,
  Phone,
  PhoneIncoming,
  Plug,
  ShieldCheck,
  Sparkles,
  User,
  Users,
  Variable,
  type LucideIcon,
} from 'lucide-react';

export type SettingsGroup =
  | 'Workspace'
  | 'Communications'
  | 'Content & Data'
  | 'Scheduling';

export interface SettingsSection {
  // Stable id — used as the localStorage recents key and the React list key.
  id: string;
  // Route segment under /settings. Empty string for the index (Overview).
  slug: string;
  label: string;
  group: SettingsGroup;
  icon: LucideIcon;
  // Hides the section from non-admins.
  adminOnly?: boolean;
  // Gates the section behind an entitlement.
  requires?: 'sms' | 'inbound';
  // Explicit destination. When set the section links here instead of the
  // conventional /settings/<slug> route — used for entries that live outside
  // the hub layout (the Agents catalogue at /agents).
  path?: string;
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  { id: 'overview', slug: '', label: 'Overview', group: 'Workspace', icon: Sparkles },
  { id: 'account', slug: 'account', label: 'Account', group: 'Workspace', icon: User },
  { id: 'team', slug: 'team', label: 'Team', group: 'Workspace', icon: Users },
  { id: 'billing', slug: 'billing', label: 'Billing', group: 'Workspace', icon: CreditCard },
  { id: 'calling', slug: 'calling', label: 'Calling', group: 'Communications', icon: Phone },
  { id: 'senders', slug: 'senders', label: 'Senders', group: 'Communications', icon: Mail },
  {
    id: 'email-deliverability',
    slug: 'email-deliverability',
    label: 'Email Deliverability',
    group: 'Communications',
    icon: ShieldCheck,
  },
  {
    id: 'inbound-config',
    slug: 'inbound-config',
    label: 'Inbound Config',
    group: 'Communications',
    icon: PhoneIncoming,
    requires: 'inbound',
  },
  { id: 'templates', slug: 'templates', label: 'Templates', group: 'Content & Data', icon: FileText },
  {
    id: 'custom-fields',
    slug: 'custom-fields',
    label: 'Custom Fields',
    group: 'Content & Data',
    icon: Variable,
  },
  { id: 'pipelines', slug: 'pipelines', label: 'Pipelines', group: 'Content & Data', icon: Layers },
  { id: 'calendars', slug: 'calendars', label: 'Calendars', group: 'Scheduling', icon: Calendar },
  {
    id: 'booking-pages',
    slug: 'booking-pages',
    label: 'Booking Pages',
    group: 'Scheduling',
    icon: CalendarCheck,
  },
  { id: 'integrations', slug: 'integrations', label: 'Integrations', group: 'Scheduling', icon: Plug },
  {
    id: 'agents',
    slug: 'agents',
    label: 'Agents',
    group: 'Communications',
    icon: Bot,
    adminOnly: true,
    path: '/agents',
  },
];

// Resolve a section to its navigation target.
export function sectionPath(section: SettingsSection): string {
  if (section.path) return section.path;
  return section.slug ? `/settings/${section.slug}` : '/settings';
}
